// 3rd party imports
import * as React from 'react';

// bernie
import { DialogRoute, RouteStore } from 'bernie-core';
import { Layer } from 'bernie-view';

// shared-ui
import { ILocalization, LocalizedText } from '@shared-ui/localization-context';

// ui
import { UitkDialog, UitkDialogContent, UitkDialogFooter } from 'uitk-react-dialog';
import { UitkHeading, UitkText } from 'uitk-react-text';
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkList, UitkListItem } from 'uitk-react-list';
import { UitkLink } from 'uitk-react-link';

// util
import { modifyLocaleParam } from '../../util/LocaleParamTransformer';
import { Program } from 'models';
import { useHistory } from 'react-router-dom';
import { LocalizedTextWrapping } from 'components/localized-text-wrapping';

export interface JoinAffiliateNetworkDialogProps {
  dialogId: string;
  l10n?: ILocalization;
  routeStore: RouteStore;
  locale: string;
  program: Program;
  isPreviewMode?: boolean;
  showManageProgramLink?: boolean;
}

export const JoinAffiliateNetworkDialog: React.FunctionComponent<JoinAffiliateNetworkDialogProps> = ({
  dialogId,
  l10n,
  routeStore,
  locale,
  program,
  isPreviewMode,
  showManageProgramLink,
}) => {
  const onDismiss = () => {
    routeStore.closeDialog();
  };

  const signUpLink = program.signUpLink?.replace(':localeName', modifyLocaleParam(locale));

  const history = useHistory();
  const handleJoinAffiliateNetwork = () => {
    if (program.network === 'PARTNERIZE') {
      history.push(signUpLink);
    } else {
      window.open(signUpLink);
    }
  };

  const manageProgramsLink = ['', modifyLocaleParam(locale), 'programs'].join('/');

  const network = program.network.toLowerCase();

  let dialogHeading = `join_program_dialog.${network}.title`;
  const dialogCtaText = l10n.formatText(`join_program_dialog.${network}.button.continue`);

  let previewDialogFirstParagraph;
  let manageProgramLinkContent;

  if (isPreviewMode) {
    dialogHeading = 'join_program_dialog.preview.title';
    previewDialogFirstParagraph = (
      <UitkText spacing="two">
        <LocalizedText message="join_program_dialog.preview.content.you_can_preview" />
      </UitkText>
    );
  }

  if (showManageProgramLink) {
    manageProgramLinkContent = (
      <UitkText spacing="two">
        <UitkText inline={true}>
          <LocalizedTextWrapping
            l10n={l10n}
            message={`join_program_dialog.${network}.have_network_account`}
            fieldName="manage_programs"
          >
            <UitkLink inline={true}>
              <a href={manageProgramsLink}>
                <LocalizedText message="join_program_dialog.link_name.manage_programs" />
              </a>
            </UitkLink>
          </LocalizedTextWrapping>
          &nbsp;
        </UitkText>
      </UitkText>
    );
  }

  const cjDialogText = () => {
    return (
      <>
        <UitkText spacing="two">
          <LocalizedText message={'join_program_dialog.cj.content.you_will_be_redirected_to_cj'} />
        </UitkText>
        <UitkText spacing="two">
          <LocalizedText message={'join_program_dialog.cj.content.please_follow_prompts_on_their_site'} />
        </UitkText>
        <UitkText spacing="two">
          <LocalizedText message={'join_program_dialog.cj.content.post_signup_instructions'} />
          <UitkSpacing padding={{ block: 'three', inline: 'four' }}>
            <UitkList>
              <UitkListItem key="listitem-1-1">
                <UitkText size={400}>
                  <LocalizedText message={'join_program_dialog.cj.content.post_signup_instructions.note_down_cid'} />
                </UitkText>
              </UitkListItem>
              <UitkListItem key="listitem-1-2">
                <UitkText size={400}>
                  <LocalizedText message={'join_program_dialog.cj.content.post_signup_instructions.await_approval'} />
                </UitkText>
              </UitkListItem>
            </UitkList>
          </UitkSpacing>
        </UitkText>
      </>
    );
  };

  let dialogText = null;
  switch (network) {
    case 'cj':
      dialogText = cjDialogText();
      break;
    case 'partnerize':
      dialogText = '';
      break;
  }

  const dialogContent = (
    <UitkDialogContent>
      <UitkHeading tag="h3" size={5}>
        <LocalizedText message={dialogHeading} />
      </UitkHeading>
      <UitkSpacing margin={{ blockstart: 'six' }}>
        <div>
          {previewDialogFirstParagraph}
          {dialogText}
          {manageProgramLinkContent}
        </div>
      </UitkSpacing>
    </UitkDialogContent>
  );

  return (
    <>
      <DialogRoute id={dialogId}>
        <Layer id="dialogJoinAffiliateNetwork" onDismiss={onDismiss}>
          <UitkDialog returnFocusOnClose={true}>
            {dialogContent}
            <UitkDialogFooter
              key="UitkDialogFooter"
              stacked={true}
              buttonOne={{
                onClick: handleJoinAffiliateNetwork,
                text: dialogCtaText,
                type: 'primary',
              }}
              buttonTwo={{
                onClick: onDismiss,
                text: l10n.formatText('join_program_dialog.preview.button.continue'),
                type: 'tertiary',
              }}
            />
          </UitkDialog>
        </Layer>
      </DialogRoute>
    </>
  );
};
